import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { AutoAssignmentRule } from '@percent/admin-dashboard/api/actions/grantProgram/grantProgram.types'
import { AddAssignmentRulesFormModal } from '@percent/admin-dashboard/app/grants/assignmentRules/addAssigmentRules/forms/AddAssignmentRulesFormModal'
import { DeleteAssignmentRuleModal } from '@percent/admin-dashboard/app/grants/assignmentRules/deleteAssignmentRuleModal/DeleteAssignmentRuleModal'
import { EditAssignmentRuleModal } from '@percent/admin-dashboard/app/grants/assignmentRules/updateAssignmentRuleModal/EditAssignmentRuleModal'
import { ErrorView } from '@percent/admin-dashboard/common/components'
import { useGrantsPermissions } from '@percent/admin-dashboard/app/grants/hooks/useGrantsPermissions'
import {
  Button,
  Flex,
  Icon,
  Menu,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableProvider,
  TableRow,
  TableSearchAndFilters
} from '@percent/lemonade'
import type { AutoAssignmentRulesProps } from './types'

enum AssignmentRuleAction {
  EDIT = 'edit',
  DELETE = 'delete',
  ADD = 'add'
}

export function AutoAssignmentRules({ rules, isLoading, errorMessage, refresh }: AutoAssignmentRulesProps) {
  const { t } = useTranslation()
  const [canUpdateProgram] = useGrantsPermissions(['can_update_program'])
  const [searchValue, setSearchValue] = useState<string>('')

  const [activeAssignmentRule, setActiveAssignmentRule] = useState<{
    action: AssignmentRuleAction
    rule: AutoAssignmentRule | null
  } | null>(null)

  const filteredRules = useMemo(() => {
    if (!searchValue.trim()) {
      return rules
    }

    const searchLower = searchValue.toLowerCase()

    return rules.filter(rule => {
      const nameMatch = rule.name?.toLowerCase().includes(searchLower) ?? false
      const taskTemplateNamesMatch =
        rule.taskTemplateNames?.some(name => name.toLowerCase().includes(searchLower)) ?? false

      return nameMatch || taskTemplateNamesMatch
    })
  }, [rules, searchValue])

  const handleClearValue = useCallback(() => {
    setSearchValue('')
  }, [])

  const closeAllModals = useCallback(() => {
    setActiveAssignmentRule(null)
  }, [])

  const getRuleMenuSections = (row: AutoAssignmentRule) => [
    {
      items: [
        ...(canUpdateProgram
          ? [
              {
                key: 'edit',
                label: t('button.edit'),
                onClick: () =>
                  setActiveAssignmentRule({
                    action: AssignmentRuleAction.EDIT,
                    rule: row
                  }),
                'data-testid': 'menu-item-edit'
              },
              {
                key: 'delete',
                label: t('button.delete'),
                onClick: () =>
                  setActiveAssignmentRule({
                    action: AssignmentRuleAction.DELETE,
                    rule: row
                  }),
                'data-testid': 'menu-item-delete'
              }
            ]
          : [])
      ]
    }
  ]

  const columns = [
    { id: 'name', content: t('typography.name') },
    { id: 'taskTemplates', content: t('typography.templateCount') },
    { id: 'accounts', content: t('typography.accountCount') },
    { id: 'countries', content: t('typography.countryCount') },
    { id: 'nominatorGroups', content: t('typography.nominatorGroupCount') },
    { id: 'actions', props: { width: '15px' }, content: ' ' }
  ]

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <Flex direction="column" gap="xs" style={{ width: '100%' }}>
      <TableProvider
        columns={columns}
        totalResults={filteredRules?.length ?? 0}
        isLoading={isLoading}
        illustration="no-results"
        emptyTableTitle={t('typography.noResultsFound')}
        emptyTableDescription=""
      >
        <TableSearchAndFilters>
          <Flex w="100%" mr={8} justify="space-between" align="center">
            <SearchInput
              width={500}
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              handleClearValue={handleClearValue}
              placeholder={t('typography.search')}
              inputSize="small"
            />
            {canUpdateProgram && (
              <Button
                variant="secondary"
                onClick={() =>
                  setActiveAssignmentRule({
                    action: AssignmentRuleAction.ADD,
                    rule: null
                  })
                }
              >
                {t('button.addAutoAssignmentRule')}
              </Button>
            )}
          </Flex>
        </TableSearchAndFilters>
        <Table>
          <TableBody>
            {filteredRules?.map(row => (
              <TableRow key={row.id} data-testid="grant-programs-row">
                <TableCell>{row.name}</TableCell>
                <TableCell>{(row.taskTemplates ?? []).length}</TableCell>
                <TableCell>{(row.accounts ?? []).length}</TableCell>
                <TableCell>{(row.countries ?? []).length}</TableCell>
                <TableCell>{(row.nominatorGroups ?? []).length}</TableCell>
                <TableCell>
                  <Menu
                    title={<Icon name="menu-vertical" size={4} color="gray300" data-testid="menu-vertical-icon" />}
                    sections={getRuleMenuSections(row)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableProvider>

      <EditAssignmentRuleModal
        isOpen={activeAssignmentRule?.action === AssignmentRuleAction.EDIT}
        onClose={closeAllModals}
        rule={activeAssignmentRule?.rule ?? null}
        refresh={refresh}
      />

      <DeleteAssignmentRuleModal
        isOpen={activeAssignmentRule?.action === AssignmentRuleAction.DELETE}
        onClose={closeAllModals}
        ruleId={activeAssignmentRule?.rule?.id ?? ''}
        ruleName={activeAssignmentRule?.rule?.name ?? ''}
        refresh={refresh}
      />

      <AddAssignmentRulesFormModal
        open={activeAssignmentRule?.action === AssignmentRuleAction.ADD}
        onClose={closeAllModals}
        onSuccess={refresh}
      />
    </Flex>
  )
}
